<!--
 * 企业中心 PC：入驻登录弹窗（左图 + 右栏，业务逻辑与 components/pc/login/pc-login 对齐）
-->
<template>
	<n-modal
		v-model:show="show"
		:closable="false"
		class="cc-login-modal"
		:class="{ 'cc-login-modal--en': isEn }"
	>
		<n-card :bordered="false" class="cc-login-card">
			<div class="cc-login-shell">
				<div class="cc-login-left">
					<img :src="loginLeftImageSrc" width="560" height="500" alt="" role="presentation" />
				</div>
				<div class="cc-login-right">
					<button class="cc-login-close" type="button" @click="handleClose">
						<img :src="closeIcon" alt="close" />
					</button>

					<div v-if="!useUser.thirdBindEmail" class="cc-login-form-wrap">
						<div v-if="showPhoneLogin && showLoginTabs" class="cc-login-tabs" role="tablist">
							<button
								type="button"
								class="cc-login-tab"
								:class="{ 'cc-login-tab--active': loginStep === 'email-input' }"
								role="tab"
								:aria-selected="loginStep === 'email-input'"
								@click="openEmailStep"
							>
								{{ t(`${I18N_NS}.loginModal.tabEmail`) }}
							</button>
							<button
								type="button"
								class="cc-login-tab"
								:class="{ 'cc-login-tab--active': loginStep === 'phone-input' }"
								role="tab"
								:aria-selected="loginStep === 'phone-input'"
								@click="openPhoneStep"
							>
								{{ t(`${I18N_NS}.loginModal.tabPhone`) }}
							</button>
						</div>

						<PcPhoneInput
							v-if="loginStep === 'phone-input'"
							variant="step"
							:show-switch="false"
							:locale="loginUiLocale"
							@next="handlePhoneNext"
						/>

						<PcEmailInput
							v-else-if="loginStep === 'email-input'"
							variant="step"
							:show-switch="false"
							:locale="loginUiLocale"
							@next="handleEmailNext"
						/>

						<PcAgreement
							v-else-if="loginStep === 'agreement'"
							:locale="loginUiLocale"
							@agree="handleAgreementAccepted"
						/>

						<PcVerification
							v-else-if="loginStep === 'verification'"
							variant="step"
							:key="verificationKey"
							:contact-info="verificationContact"
							:locale="loginUiLocale"
							@complete="handleVerificationComplete"
							@resend="handleResendCode"
						/>
					</div>

					<div v-else class="cc-login-bind">
						<div class="cc-login-bind-inner">
							<p class="cc-login-bind-title">{{ t(`${I18N_NS}.loginModal.bindTitle`) }}</p>
							<p class="cc-login-bind-desc">{{ t(`${I18N_NS}.loginModal.bindDesc`) }}</p>
							<Email
								@login-success="loginSuccess"
								:btn-text="t(`${I18N_NS}.loginModal.bindBtn`)"
								:locale="loginUiLocale"
								login-type="bind-email"
							/>
						</div>
						<div class="cc-login-bind-kefu">
							<a :href="KEFU_LINK" @click="handleKefuClick">{{
								t(`${I18N_NS}.loginModal.kefu`)
							}}</a>
						</div>
					</div>
				</div>
			</div>
		</n-card>
	</n-modal>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import useLogin from "@/hooks/login/index";
import { useUserStore } from "~/stores/user";
import { useLoginCallbackStore } from "~/stores/login-callback";
import { KEFU_LINK } from "~/utils/constants";
import eventBus from "~/utils/eventBus";
import { canShowPhoneLogin } from "~/utils/common";
import { markColdStartTagsPending } from "~/utils/coldStartTags";
import showMessage from "~/utils/showMessage";
import { useI18n } from "~/composables/useI18n";

import Email from "~/components/pc/login/pc-email.vue";
import PcAgreement from "~/components/pc/login/pc-agreement.vue";
import PcEmailInput from "~/components/pc/login/pc-email-input.vue";
import PcPhoneInput from "~/components/pc/login/pc-phone-input.vue";
import PcVerification from "~/components/pc/login/pc-verification.vue";

import closeIcon from "~/components/pc/login/images/close.png";
import loginLeftImage from "../image/login_left.png";
import loginLeftImageEn from "../image/login_left_en.png";

const props = defineProps({
	backUrl: {
		type: String,
		default: "",
	},
});

const route = useRoute();
const useUser = useUserStore();

const I18N_NS = "corporateCenter.indexPc";
const { t, locale } = useI18n();
const isEn = computed(() => locale?.value === "en");
const loginLeftImageSrc = computed(() => (isEn.value ? loginLeftImageEn : loginLeftImage));
/** 傳給登錄子組件：`zh-TW`（預設）與 `en` */
const loginUiLocale = computed(() => (locale?.value === "en" ? "en" : "zh-TW"));

const showPhoneLogin = computed(() => canShowPhoneLogin());

const showLoginTabs = computed(() => ["phone-input", "email-input"].includes(loginStep.value));

const handleKefuClick = () => {
	logEvent(`site_login_kefu_click`);
};

const show = defineModel();

const loginStep = ref("phone-input");
const selectedLoginType = ref("phone");
const contactInfo = ref("");
const selectedAreaCode = ref("");
const pendingSubmission = ref(null);
const hasAgreed = ref(false);
const verificationKey = ref(0);

const verificationContact = computed(() => {
	if (selectedLoginType.value === "phone" && selectedAreaCode.value) {
		return `${selectedAreaCode.value.replace("00", "+")}-${contactInfo.value}`;
	}
	return contactInfo.value;
});

const resetLoginState = () => {
	loginStep.value = "phone-input";
	selectedLoginType.value = "phone";
	contactInfo.value = "";
	selectedAreaCode.value = "";
	pendingSubmission.value = null;
	hasAgreed.value = false;
	verificationKey.value = 0;
};

watch(show, newVal => {
	if (newVal) {
		logEvent(`site_login_pop_show`);
		resetLoginState();
		if (!useUser.thirdBindEmail) {
			loginStep.value = showPhoneLogin.value ? "phone-input" : "email-input";
			selectedLoginType.value = showPhoneLogin.value ? "phone" : "email";
		}
	} else {
		logEvent(`site_login_pop_close`);
		setTimeout(() => {
			resetLoginState();
		}, 200);
	}
});

async function loginSuccess(type) {
	await showMessage(t(`${I18N_NS}.loginModal.loginSuccess`), "success");
	show.value = false;

	logEvent(`site_login_success`);
	markColdStartTagsPending();

	eventBus.emit("login-success", { type, backUrl: props.backUrl });

	if (props.backUrl && import.meta.client) {
		await navigateTo(
			{
				path: location.pathname,
			},
			{ replace: true }
		);
		setTimeout(() => {
			window.location.href = props.backUrl;
		}, 1000);
		return;
	}

	if (type === "reload") {
		const targetPath = window.location.pathname;

		try {
			const stateData = JSON.parse(atob(route.query.state));
			const { redirect_uri } = stateData;

			if (redirect_uri) {
				try {
					const url = new URL(redirect_uri);

					if (url.search) {
						const queryParams = new URLSearchParams(url.search);
						const query = {};
						queryParams.forEach((value, key) => {
							query[key] = value;
						});

						await navigateTo(
							{
								path: targetPath,
								query: query,
							},
							{ replace: true }
						);
					} else {
						await navigateTo(
							{
								path: targetPath,
							},
							{ replace: true }
						);
					}
				} catch (e) {
					await navigateTo({ path: targetPath }, { replace: true });
				}
			} else {
				await navigateTo(
					{
						path: targetPath,
					},
					{ replace: true }
				);
			}
		} catch (e) {
			await navigateTo({ path: targetPath }, { replace: true });
		}

		const loginCallbackStore = useLoginCallbackStore();

		if (loginCallbackStore.isActiveLogin) {
			loginCallbackStore.setActiveLogin();
			return;
		}

		eventBus.emit("third-party-login-success", {
			type,
			backUrl: props.backUrl,
		});
	}

	// 企业中心入驻场景：登录成功后进入入驻页（非外链 backUrl、非联登中断）
	if (import.meta.client) {
		await navigateTo("/seer/corporate-center/join");
	}
}

const handleClose = () => {
	show.value = false;
};

const openPhoneStep = () => {
	selectedLoginType.value = "phone";
	loginStep.value = "phone-input";
	logEvent("site_login_phone_click");
};

const openEmailStep = () => {
	selectedLoginType.value = "email";
	loginStep.value = "email-input";
	logEvent("site_login_email_click");
};

const { getCode, handleLogin, afterLineAuthLogin, afterGoogleAuthLogin } = useLogin();

const handlePhoneNext = payload => {
	pendingSubmission.value = {
		type: "phone",
		contact: payload.phone,
		areaCode: payload.areaCode,
		behavioralCaptcha: payload.behavioralCaptcha || null,
	};

	if (!hasAgreed.value) {
		loginStep.value = "agreement";
		return;
	}

	proceedPendingSubmission();
};

const handleEmailNext = email => {
	pendingSubmission.value = {
		type: "email",
		contact: email,
	};

	if (!hasAgreed.value) {
		loginStep.value = "agreement";
		return;
	}

	proceedPendingSubmission();
};

const handleAgreementAccepted = async () => {
	hasAgreed.value = true;
	await proceedPendingSubmission();
};

const proceedPendingSubmission = async () => {
	if (!pendingSubmission.value) {
		loginStep.value = selectedLoginType.value === "phone" ? "phone-input" : "email-input";
		return;
	}

	const currentSubmission = pendingSubmission.value;
	selectedLoginType.value = currentSubmission.type;
	contactInfo.value = currentSubmission.contact;
	selectedAreaCode.value = currentSubmission.areaCode || "";

	if (currentSubmission.type === "phone") {
		await getCode({
			mobile: currentSubmission.contact,
			area: currentSubmission.areaCode,
			type: "LOGIN",
			behavioral_captcha: currentSubmission.behavioralCaptcha || null,
		});
	} else {
		await getCode({
			email: currentSubmission.contact,
			type: "LOGIN",
		});
	}

	pendingSubmission.value = null;
	verificationKey.value += 1;
	loginStep.value = "verification";
};

const handleResendCode = async () => {
	if (selectedLoginType.value === "phone") {
		await getCode({
			mobile: contactInfo.value,
			area: selectedAreaCode.value,
			type: "LOGIN",
		});
		return;
	}

	await getCode({
		email: contactInfo.value,
		type: "LOGIN",
	});
};

const handleVerificationComplete = async code => {
	if (selectedLoginType.value === "phone") {
		await handleLogin(
			{
				mobile: contactInfo.value,
				code,
				area: selectedAreaCode.value,
				login_type: "mobile",
			},
			() => loginSuccess()
		);
		return;
	}

	await handleLogin(
		{
			email: contactInfo.value,
			code,
			login_type: "email",
		},
		() => loginSuccess()
	);
};

watch(
	() => route.query,
	val => {
		if (val.code && import.meta.client) {
			const currentPath = window.location.pathname;
			if (currentPath === "/seer/login") return;
			if (val.type === "line") {
				afterLineAuthLogin(val.code, () => {
					loginSuccess("reload");
				});
			} else {
				afterGoogleAuthLogin(val.code, () => {
					loginSuccess("reload");
				});
			}
		}
	},
	{
		immediate: true,
	}
);
</script>

<style lang="scss" scoped>
.cc-login-modal {
	:deep(.n-modal-body-wrapper) {
		padding: 0;
	}

	:deep(.n-card) {
		margin: 0;
		max-width: calc(100vw - 48px);
		background: transparent;
		box-shadow: none;
	}

	:deep(.n-card__content) {
		padding: 0;
	}
}

.cc-login-card {
	width: auto;
	background: transparent;
}

.cc-login-shell {
	display: flex;
	overflow: hidden;
	border-radius: 16px;
	background: #ffffff;
	box-shadow: 0 16px 48px rgba(0, 0, 0, 0.16);
}

.cc-login-left {
	flex-shrink: 0;
	width: 560px;
	height: 420px;
	overflow: hidden;

	img {
		display: block;
		width: 560px;
		height: 420px;
		object-fit: cover;
	}
}

.cc-login-right {
	position: relative;
	box-sizing: border-box;
	width: 440px;
	min-height: 420px;
	padding: 44px 36px 32px;
	display: flex;
	flex-direction: column;
}

.cc-login-close {
	position: absolute;
	top: 16px;
	right: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	padding: 0;
	background: transparent;
	border: 0;
	cursor: pointer;
	z-index: 2;

	img {
		width: 24px;
		height: 24px;
	}
}

.cc-login-tabs {
	display: flex;
	justify-content: center;
	gap: 48px;
	margin-bottom: 20px;
	padding-right: 40px;
}

.cc-login-tab {
	position: relative;
	padding: 8px 0 16px;
	margin: 0;
	background: transparent;
	border: none;
	font-size: 20px;
	font-weight: 400;
	line-height: 1.4;
	color: #b3b3b3;
	cursor: pointer;
	white-space: nowrap;

	&--active {
		color: #333333;
		font-weight: 600;

		/* 短粗橙色下划线，居中对齐文案（非整格宽度） */
		&::after {
			content: "";
			position: absolute;
			left: 50%;
			bottom: 0;
			width: 28px;
			height: 4px;
			transform: translateX(-50%);
			border-radius: 2px;
			background: #f2af19;
		}
	}
}

.cc-login-form-wrap {
	flex: 1;
	min-height: 0;
}

/* 与稿面一致：顶部仅保留隐私说明，隐藏子组件大标题 */
:deep(.phone-step .step-title),
:deep(.email-step .step-title) {
	display: none;
}

:deep(.phone-step .step-desc),
:deep(.email-step .step-desc) {
	margin-top: 12px;
	margin-bottom: 20px;
}

.cc-login-bind {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
}

.cc-login-bind-inner {
	flex: 1;
}

.cc-login-bind-title {
	margin: 0 0 12px;
	font-size: 22px;
	font-weight: 700;
	color: #222222;
	line-height: 1.4;
	text-align: center;
}

.cc-login-bind-desc {
	margin: 0 0 20px;
	font-size: 14px;
	color: #666666;
	text-align: center;
}

.cc-login-bind-kefu {
	margin-top: 16px;
	text-align: center;

	a {
		font-size: 14px;
		color: #4a7dff;
		text-decoration: none;
	}
}
</style>
